import { useEffect } from 'react';
import { useForm } from "react-hook-form";
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Database } from '@/lib/supabase/supabase_types';

const surveySchema = z.object({
    name: z.string({ message: "Field is required" }).min(1, { message: "Field is required" }),
    description: z.string().optional(),
} satisfies Record<
    keyof Pick<
        Database['public']['Tables']['survey_bank']['Insert'],
        'name' | 'description'>,
    unknown
>);
type surveySchemaType = z.infer<typeof surveySchema>;

type Props = {
    show: boolean;
    onHide: () => void;
    name?: string;
    description?: string;
    onSubmit?: (data: surveySchemaType) => void;
    isPending?: boolean;
}

export const DuplicateSurveyTemplateDialog = ({
    show,
    onHide,
    name,
    description,
    onSubmit,
    isPending = false,
}: Props) => {

    const form = useForm<surveySchemaType>({ resolver: zodResolver(surveySchema) });

    useEffect(() => {
        if(show) {
            form.reset({
                name: name || '',
                description: description || ''
            });
        }
    }, [show])

    const onCloseCreateSurveyModal = () => {
        onHide();
        form.reset({
            name: '',
            description: ''
        });
    }

    const onSubmitHandler = (data: surveySchemaType) => {
        if (onSubmit) {
            onSubmit(data);
        }
    }

    return (
        <>
            <Dialog open={show} onOpenChange={onCloseCreateSurveyModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Duplicate survey template
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            You can adjust the name and description for this duplicate survey.
                        </DialogDescription>
                    </DialogHeader>

                    <Form {...form}>
                        <form className="px-1 py-1 space-y-2">
                            <FormField
                                control={form.control}
                                name='name'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Survey Name</FormLabel>
                                        <FormControl>
                                            <Input {...field}/>
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='description'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Survey Description</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </form>
                    </Form>

                    <DialogFooter>
                        <div className="flex items-stretch justify-end m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                            <Button size="sm" variant="secondary" onClick={onCloseCreateSurveyModal} disabled={isPending}>
                                Cancel
                            </Button>
                            <Button size="sm" onClick={form.handleSubmit(onSubmitHandler)} disabled={isPending}>
                                { isPending && (<Loader size="16" className="animate-spin"/>)}
                                Submit
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
